import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Switch,
  TouchableOpacity,
  Linking,
  Platform,
  Alert,
  ActivityIndicator,
  AppState,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import * as Notifications from "expo-notifications";
import { PermissionStatus } from "expo-modules-core";
import { useColors, ThemeColors } from "../hooks/useColors";
import { useAuth } from "../hooks/useAuth";
import { useExpoPushNotifications } from "../hooks/useExpoPushNotifications";
import { spacing, typography } from "../theme";

// Define what types of notifications users can receive
const NOTIFICATION_TYPES = [
  {
    id: "order_updates",
    icon: "package" as const,
    title: "Order Updates",
    description:
      "Get notified when your order status changes, including confirmation, shipping, and delivery updates.",
  },
  {
    id: "messages",
    icon: "message-circle" as const,
    title: "Messages",
    description:
      "Receive notifications when sellers or buyers send you messages through the app.",
  },
  {
    id: "delivery_status",
    icon: "truck" as const,
    title: "Delivery Status",
    description:
      "Real-time updates about your delivery, including driver location and estimated arrival time.",
  },
  {
    id: "promotional",
    icon: "tag" as const,
    title: "Promotions & Offers",
    description:
      "Occasional updates about special deals, discounts, and new features in JadeTrade.",
  },
];

export default function NotificationSettingsScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const { user } = useAuth();
  const {
    permissionStatus,
    pushToken,
    registerAsync,
    unregisterAsync,
    isRegistering,
    isLoadingToken,
    isDevice,
    loadExistingToken,
  } = useExpoPushNotifications();

  const [isToggling, setIsToggling] = useState(false);

  // Load existing token on mount to restore switch state
  useEffect(() => {
    if (user?.id && isDevice) {
      loadExistingToken(user.id);
    }
  }, [user?.id, isDevice, loadExistingToken]);

  // Notifications are enabled only if we have permission AND a registered token
  const hasPermission = permissionStatus === PermissionStatus.GRANTED;
  const isEnabled = hasPermission && !!pushToken;

  const openAppSettings = useCallback(async () => {
    try {
      if (Platform.OS === "ios") {
        await Linking.openURL("app-settings:");
      } else {
        await Linking.openSettings();
      }
    } catch (error) {
      Alert.alert(
        "Unable to Open Settings",
        "Please open your device settings manually and navigate to the JadeTrade app to manage notification permissions."
      );
    }
  }, []);

  const handleToggleNotifications = useCallback(async () => {
    if (!user?.id) {
      Alert.alert(
        "Sign In Required",
        "Please sign in to manage notification settings."
      );
      return;
    }

    if (!isDevice) {
      Alert.alert(
        "Device Required",
        "Push notifications are only available on physical devices."
      );
      return;
    }


    setIsToggling(true);

    try {
      if (isEnabled) {
        // User wants to turn OFF notifications - unregister the token
        await unregisterAsync(user.id);
      } else {
        // User wants to turn ON notifications
        const result = await registerAsync(user.id);

        if (result.status !== PermissionStatus.GRANTED) {
          // Permission was denied or not granted
          Alert.alert(
            "Permission Required",
            "JadeTrade needs notification permissions to send you updates. " +
              "Please enable notifications in your device settings.",
            [
              { text: "Cancel", style: "cancel" },
              { text: "Open Settings", onPress: openAppSettings },
            ]
          );
        }
      }
    } catch (error) {
      console.error("Error toggling notifications:", error);
      Alert.alert(
        "Error",
        "There was an issue updating your notification settings. Please try again."
      );
    } finally {
      setIsToggling(false);
    }
  }, [
    user?.id,
    isDevice,
    isEnabled,
    registerAsync,
    unregisterAsync,
    openAppSettings,
  ]);

  // Re-check permission status when app comes back to foreground (e.g., returning from settings)
  useEffect(() => {
    const checkPermissions = async () => {
      await Notifications.getPermissionsAsync();
    };
    
    // Check on mount
    checkPermissions();
    
    // Listen for app state changes to refresh when returning from settings
    const subscription = AppState.addEventListener("change", (nextAppState) => {
      if (nextAppState === "active") {
        checkPermissions();
        // If user has permission and we have their ID, try to re-register
        if (user?.id && isDevice) {
          registerAsync(user.id).catch(() => {
            // Silently fail - user may have denied permission
          });
        }
      }
    });

    return () => {
      subscription.remove();
    };
  }, [user?.id, isDevice, registerAsync]);

  // Check if user has actually interacted with permissions before
  // On Android, DENIED can mean "never asked" or "actually denied"
  // We use pushToken to help determine if they've set up notifications before
  const hasBeenSetUp = !!pushToken || hasPermission;

  const getStatusColor = () => {
    if (isEnabled) return colors.success;
    if (hasPermission && !pushToken) return colors.textLight; // Permission granted but turned off
    if (!hasBeenSetUp) return colors.textLight; // Never set up
    return colors.textLight;
  };

  const getStatusText = () => {
    if (isEnabled) return "Enabled";
    if (hasPermission && !pushToken) return "Disabled";
    return "Not Set Up";
  };

  return (
    <ScrollView style={styles.container}>
      {/* Header Section */}
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <Feather name="bell" size={48} color={colors.primary} />
        </View>
        <Text style={styles.title}>Notification Settings</Text>
        <Text style={styles.subtitle}>
          Control what notifications you receive from JadeTrade
        </Text>
      </View>

      <View style={styles.content}>
        {/* Main Toggle Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Push Notifications</Text>

          <View style={styles.toggleCard}>
            <View style={styles.toggleInfo}>
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: getStatusColor() + "20" },
                ]}
              >
                <View
                  style={[
                    styles.statusDot,
                    { backgroundColor: getStatusColor() },
                  ]}
                />
                <Text style={[styles.statusText, { color: getStatusColor() }]}>
                  {getStatusText()}
                </Text>
              </View>
              <Text style={styles.toggleTitle}>Allow Notifications</Text>
              <Text style={styles.toggleDescription}>
                Enable to receive important updates about your orders, messages, and deliveries
              </Text>
            </View>
            <View style={styles.switchContainer}>
              {isToggling || isRegistering || isLoadingToken ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <Switch
                  value={isEnabled}
                  onValueChange={handleToggleNotifications}
                  trackColor={{
                    false: colors.border,
                    true: colors.primaryLight,
                  }}
                  thumbColor={isEnabled ? colors.primary : colors.textLight}
                  ios_backgroundColor={colors.border}
                  disabled={!isDevice}
                />
              )}
            </View>
          </View>

          {/* Settings Link Card */}
          <TouchableOpacity
            style={styles.settingsLinkCard}
            onPress={openAppSettings}
          >
            <View style={styles.settingsLinkContent}>
              <Feather name="settings" size={20} color={colors.primary} />
              <View style={styles.settingsLinkText}>
                <Text style={styles.settingsLinkTitle}>
                  Open Device Settings
                </Text>
                <Text style={styles.settingsLinkDescription}>
                  Manage notification permissions directly in your{" "}
                  {Platform.OS === "ios" ? "iPhone" : "Android"} settings
                </Text>
              </View>
            </View>
            <Feather name="external-link" size={20} color={colors.textLight} />
          </TouchableOpacity>
        </View>

        {/* Notification Types Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>What You'll Receive</Text>
          <Text style={styles.sectionDescription}>
            When notifications are enabled, you'll receive alerts for the
            following:
          </Text>

          <View style={styles.notificationTypesList}>
            {NOTIFICATION_TYPES.map((type, index) => (
              <View
                key={type.id}
                style={[
                  styles.notificationTypeItem,
                  index === NOTIFICATION_TYPES.length - 1 &&
                    styles.lastNotificationItem,
                ]}
              >
                <View
                  style={[
                    styles.notificationTypeIcon,
                    !isEnabled && styles.notificationTypeIconDisabled,
                  ]}
                >
                  <Feather
                    name={type.icon}
                    size={20}
                    color={isEnabled ? colors.primary : colors.textLight}
                  />
                </View>
                <View style={styles.notificationTypeContent}>
                  <Text
                    style={[
                      styles.notificationTypeTitle,
                      !isEnabled && styles.textDisabled,
                    ]}
                  >
                    {type.title}
                  </Text>
                  <Text
                    style={[
                      styles.notificationTypeDescription,
                      !isEnabled && styles.textDisabled,
                    ]}
                  >
                    {type.description}
                  </Text>
                </View>
                {isEnabled && (
                  <Feather name="check" size={18} color={colors.success} />
                )}
              </View>
            ))}
          </View>
        </View>

      </View>
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      padding: spacing.xl,
      alignItems: "center",
      backgroundColor: colors.backgroundSecondary,
    },
    iconContainer: {
      marginBottom: spacing.md,
    },
    title: {
      ...typography.title,
      fontSize: 28,
      fontWeight: "700",
      color: colors.text,
      textAlign: "center",
      marginBottom: spacing.sm,
    },
    subtitle: {
      ...typography.body,
      fontSize: 16,
      color: colors.textLight,
      textAlign: "center",
      lineHeight: 22,
    },
    content: {
      padding: spacing.sm,
    },
    section: {
      marginBottom: spacing.xl,
    },
    sectionTitle: {
      ...typography.subtitle,
      fontSize: 20,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    sectionDescription: {
      ...typography.body,
      fontSize: 14,
      color: colors.textLight,
      marginBottom: spacing.md,
      lineHeight: 20,
    },
    toggleCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: spacing.lg,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      borderWidth: 1,
      borderColor: colors.border,
      marginBottom: spacing.sm,
    },
    toggleInfo: {
      flex: 1,
      marginRight: spacing.md,
    },
    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      alignSelf: "flex-start",
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.xs,
      borderRadius: 12,
      marginBottom: spacing.sm,
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      marginRight: spacing.xs,
    },
    statusText: {
      fontSize: 12,
      fontWeight: "600",
    },
    toggleTitle: {
      ...typography.subtitle,
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.xs,
    },
    toggleDescription: {
      ...typography.caption,
      fontSize: 13,
      color: colors.textLight,
      lineHeight: 18,
    },
    switchContainer: {
      minWidth: 50,
      alignItems: "center",
      justifyContent: "center",
    },
    settingsLinkCard: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 12,
      padding: spacing.md,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    settingsLinkContent: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
      gap: spacing.md,
    },
    settingsLinkText: {
      flex: 1,
      marginRight: spacing.md,
    },
    settingsLinkTitle: {
      ...typography.body,
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 2,
    },
    settingsLinkDescription: {
      ...typography.caption,
      fontSize: 12,
      color: colors.textLight,
    },
    notificationTypesList: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: "hidden",
    },
    notificationTypeItem: {
      flexDirection: "row",
      alignItems: "flex-start",
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderLight,
    },
    lastNotificationItem: {
      borderBottomWidth: 0,
    },
    notificationTypeIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.primaryLight,
      alignItems: "center",
      justifyContent: "center",
      marginRight: spacing.md,
    },
    notificationTypeIconDisabled: {
      backgroundColor: colors.backgroundSecondary,
    },
    notificationTypeContent: {
      flex: 1,
      marginRight: spacing.sm,
    },
    notificationTypeTitle: {
      ...typography.body,
      fontSize: 15,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 4,
    },
    notificationTypeDescription: {
      ...typography.caption,
      fontSize: 13,
      color: colors.textLight,
      lineHeight: 18,
    },
    textDisabled: {
      opacity: 0.5,
    },
  });

